import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FileText,
  PenTool,
  UserCheck,
  UserPlus,
  X,
  ArrowRight,
  ArrowLeft,
  Layers,
  Calendar,
  Loader2,
  CheckCircle2,
  Search,
} from "lucide-react";
import { usePostRequest, useGetRequest } from "@/shared/hooks/requests";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';
import {
  ENROLL_SIGNED,
  ENROLL_AGREEMENT,
  SHARE_USERS,
  WORKFLOW_USERS_WITH_CERTS,
} from "@/shared/utils/urls";
import { isSignable } from "@/shared/utils/fileKind";
import { SignerRow } from "@/modules/signing/components/SignerRow.jsx";
import { signerRowsProblem } from "@/modules/my-docs/hooks/useSignerList.js";
import { placeholderErrorMessages } from "@/modules/signing/placeholderCheck";
import { MAX_MESSAGE_LENGTH, deadlineProblem, noticePayload, toLocalInput } from "@/modules/signing/notice";
import { Globe, Lock, EyeOff, QrCode } from "lucide-react";

const VISIBILITIES = [
  { value: 'restricted', icon: Lock },
  { value: 'public', icon: Globe },
  { value: 'off', icon: EyeOff },
];

// Who may open the verification page the stamped QR points at.
const VisibilityPicker = ({ value, onChange, t }) => (
  <div className="space-y-3">
    <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1 flex items-center gap-1.5">
      <QrCode className="w-3.5 h-3.5" />
      {t('signing:verification.visibility')}
    </Label>
    <p className="text-xs text-muted-foreground pl-1 -mt-1">{t('signing:verification.enrollHint')}</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {VISIBILITIES.map((v) => {
        const Icon = v.icon;
        const active = value === v.value;
        return (
          <button
            key={v.value}
            type="button"
            onClick={() => onChange(v.value)}
            className={`flex items-start gap-2 p-3 rounded-lg border text-left transition-all cursor-pointer ${active ? 'border-primary bg-primary/10 shadow-sm' : 'border-border hover:border-border/80 bg-background'}`}
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t(`signing:verification.visibilityOptions.${v.value}.label`)}</p>
              <p className="text-[11px] text-muted-foreground leading-snug">{t(`signing:verification.visibilityOptions.${v.value}.hint`)}</p>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const documentTypes = [
  {
    value: 'ordinary',
    icon: FileText,
    labelKey: 'signing:approvalState.ordinary',
    descriptionKey: 'signing:approvalState.ordinaryDesc',
    color: 'text-gray-600',
  },
  {
    value: 'agreement',
    icon: UserCheck,
    labelKey: 'signing:approvalState.agreement',
    descriptionKey: 'signing:approvalState.agreementDesc',
    color: 'text-blue-600',
  },
  {
    value: 'signed',
    icon: PenTool,
    labelKey: 'signing:approvalState.signed',
    descriptionKey: 'signing:approvalState.signedDesc',
    color: 'text-purple-600',
  },
];

const signingModes = [
  {
    value: 'sequential',
    icon: ArrowRight,
    labelKey: 'signing:mode.sequential',
    descriptionKey: 'signing:mode.sequentialDesc',
  },
  {
    value: 'parallel',
    icon: Layers,
    labelKey: 'signing:mode.parallel',
    descriptionKey: 'signing:mode.parallelDesc',
  },
];

export const DocumentTypeSelector = ({
  open,
  onOpenChange,
  fileId,
  fileName,
  currentType = 'ordinary',
  onSuccess,
}) => {
  const { t } = useTranslation();

  // Step management: 1 = select type, 2 = configure signers
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(currentType);
  // Who may open the verification page the stamped QR points at.
  const [visibility, setVisibility] = useState('restricted');
  // Enrolling for signing renders a stamped PDF through Collabora, so only
  // formats it can convert (plus PDF) may become `signed`.
  const signable = !fileName || isSignable({ name: fileName });

  // Signer configuration
  const [signingMode, setSigningMode] = useState('sequential');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const deadlineIssue = deadlineProblem(expiresAt);
  const [signers, setSigners] = useState([
    { user_id: null, national_id_value: '', full_name: '', email: '', signer_order: 0 }
  ]);
  const [loadingExisting] = useState(false);

  const enrollSigned = usePostRequest({
    url: ENROLL_SIGNED.replace('{file_id}', fileId),
    // the project may require a template's fields filled first
    errorMessages: placeholderErrorMessages,
  });

  const loadProjectUsers = useGetRequest({ url: SHARE_USERS });
  const loadProjectUsersWithCerts = useGetRequest({ url: WORKFLOW_USERS_WITH_CERTS });
  const currentUserId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;

  useEffect(() => {
    if (step === 2 && selectedType === 'agreement') {
      loadProjectUsers.request();
    }
    if (step === 2 && selectedType === 'signed') {
      loadProjectUsersWithCerts.request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedType]);

  const projectUsersWithCerts = useMemo(() => {
    return loadProjectUsersWithCerts.response || [];
  }, [loadProjectUsersWithCerts.response]);

  const availableProjectUsers = useMemo(() => {
    return loadProjectUsers.response || [];
  }, [loadProjectUsers.response]);


  // Agreement participant picker state (separate from signed-mode signers
  // but uses the same project-users list).
  const [agreementSelectedUsers, setAgreementSelectedUsers] = useState([]);
  const [agreementUserSearch, setAgreementUserSearch] = useState('');

  const filteredAgreementUsers = useMemo(() => {
    if (!agreementUserSearch) return availableProjectUsers;
    const q = agreementUserSearch.toLowerCase();
    return availableProjectUsers.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || u.display_name || '').toLowerCase().includes(q)
    );
  }, [availableProjectUsers, agreementUserSearch]);

  const toggleAgreementUser = (user) => {
    setAgreementSelectedUsers((prev) => {
      const exists = prev.some(p => p.id === user.id);
      return exists ? prev.filter(p => p.id !== user.id) : [...prev, user];
    });
  };

  const enrollAgreement = usePostRequest({
    url: ENROLL_AGREEMENT.replace('{file_id}', fileId),
    errorMessages: placeholderErrorMessages,
  });

  // State machine: ordinary -> agreement -> signed. Signed is terminal.
  // From `agreement`, the only valid transition is to `signed` (the
  // agreement workflow is deleted server-side; participants do NOT carry
  // over — the owner adds E-IMZO signers afterward).
  const isLocked = currentType === 'signed';
  const isFromAgreement = currentType === 'agreement';
  const availableTypes = isFromAgreement
    ? documentTypes.filter((t) => t.value === 'signed')
    : documentTypes;

  // No need to fetch existing request from public endpoint anymore
  useEffect(() => {
    if (open) {
      setStep(1);
      // From agreement, the only target is `signed` — preselect it so the
      // user doesn't see the radio sitting on the no-op "agreement" row.
      setSelectedType(isFromAgreement ? 'signed' : currentType);
    } else {
      // Reset state when closed
      setSigningMode('sequential');
      setMessage('');
      setExpiresAt('');
      setSigners([{ user_id: null, national_id_value: '', full_name: '', email: '', signer_order: 0 }]);
      setAgreementSelectedUsers([]);
      setAgreementUserSearch('');
      setVisibility('restricted');
    }
  }, [open, fileId, currentType]);

  const handleAddSigner = () => {
    setSigners([
      ...signers,
      { user_id: null, national_id_value: '', full_name: '', email: '', signer_order: signers.length }
    ]);
  };

  const handleRemoveSigner = (index) => {
    if (signers.length <= 1) return;
    const newSigners = signers.filter((_, i) => i !== index);
    setSigners(newSigners.map((s, i) => ({ ...s, signer_order: i })));
  };

  const handleSignerRowChange = (index, updatedRow) => {
    setSigners(prev => {
      const next = [...prev];
      next[index] = { ...updatedRow, signer_order: index };
      return next;
    });
  };

  const handleNextStep = async () => {
    if (isLocked) {
      onOpenChange(false);
      return;
    }
    if (selectedType === 'ordinary' || selectedType === currentType) {
      onOpenChange(false);
      return;
    }
    // Both 'agreement' and 'signed' need a step 2 to configure participants/signers.
    setStep(2);
  };

  const handleAgreementSubmit = async () => {
    if (isLocked) {
      toast.error(t('signing:approvalState.locked'));
      onOpenChange(false);
      return;
    }
    if (agreementSelectedUsers.length === 0) {
      toast.error(t('signing:agreement.noParticipantSelected'));
      return;
    }
    try {
      const { success } = await enrollAgreement.request({
        data: {
          procedure_type: signingMode,
          participants: agreementSelectedUsers.map((u, i) => ({
            user_id: u.id,
            participant_order: signingMode === 'sequential' ? i + 1 : 1,
          })),
        },
      });
      if (success) {
        toast.success(t('signing:agreement.created') || t('signing:approvalState.updated'));
        onOpenChange(false);
        if (onSuccess) onSuccess(selectedType);
      } else {
        toast.error(t('signing:agreement.createError') || t('signing:approvalState.error'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('signing:agreement.createError') || t('signing:approvalState.error'));
    }
  };

  const handleSubmitWithSigners = async () => {
    if (deadlineIssue) {
      toast.error(t(`signing:notice.deadline_${deadlineIssue}`));
      return;
    }
    // Validate signers
    const usableRows = signers.filter(s => s.user_id != null || (s.national_id_value || '').trim());
    if (usableRows.length === 0) {
      toast.error(t('signing:addSigners.noSigners'));
      return;
    }

    const rowProblem = signerRowsProblem(usableRows);
    if (rowProblem) {
      toast.error(rowProblem.problem === 'pinfl'
        ? t('signing:addSigners.invalidPinfl', { pinfl: rowProblem.value })
        : t('signing:addSigners.invalidEmail'));
      return;
    }

    const candidatesPayload = usableRows.map((s, i) => ({
      ...(s.user_id != null ? { user_id: s.user_id } : {}),
      ...(s.user_id != null && s.certificate_id != null ? { certificate_id: s.certificate_id } : {}),
      ...((s.national_id_value || '').trim() ? { national_id_value: s.national_id_value.trim() } : {}),
      full_name: (s.full_name || '').trim() || null,
      email: (s.email || '').trim() || null,
      participant_order: signingMode === 'sequential' ? i : 0,
    }));

    if (isLocked) {
      toast.error(t('signing:approvalState.locked'));
      onOpenChange(false);
      return;
    }

    try {
      const { success } = await enrollSigned.request({
        data: {
          sign_procedure_type: signingMode,
          candidates: candidatesPayload,
          visibility,
          ...noticePayload({ message, expiresAt }),
        },
      });

      if (success) {
        toast.success(t('signing:addSigners.created'));
        onOpenChange(false);
        if (onSuccess) onSuccess(selectedType);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('signing:addSigners.error'));
    }
  };

  const handleSaveWithoutSigners = async () => {
    if (deadlineIssue) {
      toast.error(t(`signing:notice.deadline_${deadlineIssue}`));
      return;
    }
    if (isLocked) {
      toast.error(t('signing:approvalState.locked'));
      onOpenChange(false);
      return;
    }
    try {
      const { success } = await enrollSigned.request({
        data: {
          sign_procedure_type: signingMode,
          candidates: [],
          visibility,
          ...noticePayload({ message, expiresAt }),
        },
      });

      if (success) {
        toast.success(t('signing:approvalState.updated'));
        onOpenChange(false);
        if (onSuccess) onSuccess(selectedType);
      }
    } catch (err) {
      console.error(err);
      toast.error(t('signing:addSigners.error'));
    }
  };

  const isLoading = enrollSigned.loading || enrollAgreement.loading;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn(
          "max-h-[90vh] overflow-y-auto p-0 border-none bg-background shadow-2xl ring-1 ring-border/50",
          // Step 2 with signed mode shows the full PINFL + User + Name + Email
          // row per signer — needs space to avoid horizontal scroll.
          step === 2 && selectedType === 'signed' ? "sm:max-w-5xl" : "sm:max-w-xl",
        )}>
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                {step === 1
                  ? t('signing:approvalState.title')
                  : t('signing:addSigners.titleSign')
                }
              </DialogTitle>
              <DialogDescription className="text-[15px] pt-1 leading-relaxed">
                {step === 1
                  ? t('signing:approvalState.description')
                  : t('signing:addSigners.description')
                }
              </DialogDescription>
            </DialogHeader>

            {loadingExisting ? (
              // Loading state
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : isLocked ? (
              // Type is set once and cannot be changed
              <div className="space-y-4">
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  {t('signing:approvalState.lockedHint')}
                </div>
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    className="rounded-md font-medium px-4"
                  >
                    {t('common:close')}
                  </Button>
                </div>
              </div>
            ) : step === 1 ? (
              // Step 1: Select Document Type
              <div className="space-y-4">
                <RadioGroup
                  value={selectedType}
                  onValueChange={setSelectedType}
                  className="grid grid-cols-1 gap-4"
                >
                  {availableTypes.map((type) => {
                    const Icon = type.icon;
                    const isActive = selectedType === type.value;
                    const blocked = type.value === 'signed' && !signable;
                    return (
                      <div
                        key={type.value}
                        className={`group relative flex items-start space-x-4 p-5 rounded-md border transition-all duration-300 shadow-sm ${blocked
                          ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                          : isActive
                            ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20 cursor-pointer hover:shadow-md'
                            : 'border-border bg-card hover:border-primary/30 hover:bg-accent/50 cursor-pointer hover:shadow-md'
                          }`}
                        title={blocked ? t('signing:verification.unsignableHint') : undefined}
                        onClick={() => !blocked && setSelectedType(type.value)}
                      >
                        <div className={`mt-1 p-2 rounded-md transition-colors duration-300 ${isActive ? 'bg-primary text-white shadow-lg shadow-primary/30' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <Label
                              htmlFor={`doctype-${type.value}`}
                              className="text-lg font-semibold leading-none cursor-pointer group-hover:text-primary transition-colors"
                            >
                              {t(type.labelKey)}
                            </Label>
                            <RadioGroupItem
                              value={type.value}
                              id={`doctype-${type.value}`}
                              className={`border-2 transition-transform duration-300 ${isActive ? 'scale-110 border-primary text-primary' : 'border-muted-foreground/30'}`}
                            />
                          </div>
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            {t(type.descriptionKey)}
                          </p>
                          {blocked && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                              {t('signing:verification.unsignableHint')}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </RadioGroup>

                <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    className="rounded-md font-medium px-4"
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button
                    onClick={handleNextStep}
                    disabled={isLoading}
                    className="font-semibold text-white h-11 px-8"
                    style={{
                      background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                      border: 'none',
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : selectedType === 'ordinary' ? (
                      t('common:save')
                    ) : (
                      <>
                        {t('signing:approvalState.next')}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : selectedType === 'agreement' ? (
              // Step 2 (agreement): pick participants
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:addSigners.signingMode')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'sequential', icon: ArrowRight, label: t('signing:mode.sequential') },
                      { value: 'parallel', icon: Layers, label: t('signing:mode.parallel') }
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setSigningMode(mode.value)}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${signingMode === mode.value ? 'border-primary bg-primary/10 shadow-sm' : 'border-border hover:border-border/80 bg-background'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${signingMode === mode.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                          <mode.icon className="w-4 h-4" />
                        </div>
                        <p className="text-sm font-medium text-foreground">{mode.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {agreementSelectedUsers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1">
                      {t('signing:agreement.selected')} ({agreementSelectedUsers.length})
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {agreementSelectedUsers.map((p, idx) => (
                        <div key={p.id} className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium border border-blue-200">
                          {signingMode === 'sequential' && (
                            <span className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>
                          )}
                          <span className="truncate max-w-[140px]">{p.displayName || p.display_name || p.email}</span>
                          <button type="button" onClick={() => toggleAgreementUser(p)} className="hover:bg-blue-200 rounded-full p-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:mode.project-users')}
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('signing:mode.searchPlaceholder')}
                      value={agreementUserSearch}
                      onChange={(e) => setAgreementUserSearch(e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                  <div className="border border-border rounded-md max-h-[280px] overflow-y-auto">
                    {loadProjectUsers.loading ? (
                      <div className="flex justify-center p-4">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                    ) : filteredAgreementUsers.length === 0 ? (
                      <div className="text-center text-xs text-muted-foreground py-6">
                        {agreementUserSearch ? t('signing:mode.noMatchingUsers') : t('signing:mode.noProjectUsers')}
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {filteredAgreementUsers.map((user) => {
                          const isSelected = agreementSelectedUsers.some(p => p.id === user.id);
                          const isSelf = String(user.id) === String(currentUserId);
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => toggleAgreementUser(user)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-muted/50'}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${isSelected ? 'bg-blue-500 text-white' : 'bg-muted text-foreground'}`}>
                                {(user.email || 'U')[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.displayName || user.display_name || user.email}
                                  {isSelf && (
                                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                                      {t('signing:mode.youInitiator')}
                                    </span>
                                  )}
                                </p>
                                {(user.displayName || user.display_name) && user.email && (
                                  <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                                )}
                              </div>
                              {isSelected && (
                                <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0">
                                  <UserCheck className="w-3 h-3" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="font-medium px-4 h-9 flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t('signing:approvalState.back')}
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onOpenChange(false)}
                      className="font-medium px-4 h-9"
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAgreementSubmit}
                      disabled={enrollAgreement.loading || agreementSelectedUsers.length === 0}
                      className="font-semibold text-white h-9 px-6"
                      style={{
                        background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                      }}
                    >
                      {enrollAgreement.loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {t('signing:agreement.create')}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : isFromAgreement ? (
              // Step 2 (signed, from agreement): no signer entry — the
              // backend deletes the agreement workflow; the owner adds
              // E-IMZO signers afterward from the signing panel.
              <div className="space-y-6">
                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 space-y-2">
                  <p className="font-semibold">
                    {t('signing:approvalState.cascadeTitle')}
                  </p>
                  <p className="leading-relaxed">
                    {t('signing:approvalState.cascadeHint')}
                  </p>
                </div>

                <div className="space-y-3">
                  <Label className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:addSigners.signingMode')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'sequential', icon: ArrowRight, label: t('signing:mode.sequential') },
                      { value: 'parallel', icon: Layers, label: t('signing:mode.parallel') }
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setSigningMode(mode.value)}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${signingMode === mode.value ? 'border-primary bg-primary/10 shadow-sm' : 'border-border hover:border-border/80 bg-background'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${signingMode === mode.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                          <mode.icon className="w-4 h-4" />
                        </div>
                        <p className="text-sm font-medium text-foreground">{mode.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <VisibilityPicker value={visibility} onChange={setVisibility} t={t} />

                <div className="flex items-center justify-between gap-3 pt-4 border-t">
                  <Button type="button" variant="ghost" onClick={() => setStep(1)} className="font-medium px-4 h-9 flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    {t('signing:approvalState.back')}
                  </Button>
                  <div className="flex gap-3">
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="font-medium px-4 h-9">
                      {t('common:cancel')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSaveWithoutSigners}
                      disabled={enrollSigned.loading}
                      className="font-semibold text-white h-9 px-6"
                      style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
                    >
                      {enrollSigned.loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {t('signing:approvalState.cascadeConfirm')}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              // Step 2 (signed): configure E-IMZO signers
              <div className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-base font-bold tracking-tight">{t('signing:addSigners.signingMode')}</Label>
                  <RadioGroup
                    value={signingMode}
                    onValueChange={(val) => setSigningMode(val)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    {signingModes.map((mode) => {
                      const Icon = mode.icon;
                      const isActive = signingMode === mode.value;
                      return (
                        <div
                          key={mode.value}
                          className={`relative flex items-center space-x-3 p-4 rounded-md border transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md ${isActive
                            ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20'
                            : 'border-border bg-card hover:border-primary/30'
                            }`}
                          onClick={() => setSigningMode(mode.value)}
                        >
                          <div className={`p-2 rounded-md ${isActive ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-muted text-muted-foreground'}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <div className={`font-bold text-sm ${isActive ? 'text-primary' : 'text-foreground'}`}>{t(mode.labelKey)}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{t(mode.descriptionKey)}</div>
                          </div>
                          <RadioGroupItem
                            value={mode.value}
                            id={`mode-${mode.value}`}
                            className="sr-only"
                          />
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>

                {<VisibilityPicker value={visibility} onChange={setVisibility} t={t} />}

                {/* Unified signer rows: user-picker + PINFL together */}
                {(
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pl-1">
                      <Label className="text-base font-bold tracking-tight">
                        {t('signing:addSigners.signers')}
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddSigner}
                        className="rounded-md h-9 border-primary/30 text-primary hover:bg-primary/5 hover:border-primary transition-all font-semibold"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        {t('signing:addSigners.addSigner')}
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {signers.map((row, index) => (
                        <SignerRow
                          key={index}
                          row={row}
                          index={index}
                          signingMode={signingMode}
                          projectUsersWithCerts={projectUsersWithCerts}
                          loadingUsers={loadProjectUsersWithCerts.loading}
                          currentUserId={currentUserId}
                          canRemove={signers.length > 1}
                          onChange={(updated) => handleSignerRowChange(index, updated)}
                          onRemove={() => handleRemoveSigner(index)}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {signingMode === 'sequential' && (
                  <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-md border border-border/50">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <p className="text-xs font-semibold text-muted-foreground leading-tight">
                      {t('signing:addSigners.sequentialHint')}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="signers-message" className="text-sm font-bold tracking-tight">{t('signing:addSigners.message')}</Label>
                    <Textarea
                      id="signers-message"
                      placeholder={t('signing:addSigners.messagePlaceholder')}
                      value={message}
                      maxLength={MAX_MESSAGE_LENGTH}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                      className="rounded-md bg-background/50 border-border focus:ring-primary/20 resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signers-expires-at" className="text-sm font-bold tracking-tight flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      {t('signing:addSigners.expiresAt')}
                    </Label>
                    <Input
                      id="signers-expires-at"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      min={toLocalInput(new Date())}
                      aria-invalid={deadlineIssue ? true : undefined}
                      aria-describedby={deadlineIssue ? 'signers-expires-at-error' : undefined}
                      className="h-10 rounded-md bg-background/50 border-border focus:ring-primary/20"
                    />
                    {deadlineIssue && (
                      <p id="signers-expires-at-error" role="alert" className="text-xs text-red-600 dark:text-red-400">
                        {t(`signing:notice.deadline_${deadlineIssue}`)}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground leading-tight px-1 italic">
                      {t('signing:addSigners.expiresHint')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 pt-6 border-t mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => setStep(1)}
                    className="rounded-md font-medium px-4 h-11"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('signing:approvalState.back')}
                  </Button>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      onClick={handleSaveWithoutSigners}
                      disabled={isLoading || Boolean(deadlineIssue)}
                      className="rounded-md font-medium px-4 h-11"
                    >
                      {t('signing:addSigners.saveWithoutSigners')}
                    </Button>
                    {(
                      <Button
                        onClick={handleSubmitWithSigners}
                        disabled={isLoading || Boolean(deadlineIssue)}
                        className="font-semibold text-white h-11 px-8"
                        style={{
                          background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                          border: 'none',
                        }}
                      >
                        {isLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          t('signing:addSigners.submit')
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default DocumentTypeSelector;
